/**
 * CorrespondenceReviewQueue - Sprint Y Block G
 * 
 * The HITL (Human-in-the-Loop) commit gate for correspondence regulatory data.
 * This is the ONLY place in the system where regulatory links get committed.
 * 
 * Features:
 * - Lists ALL correspondence with needs_review=True
 * - Ordered by link_confidence ASC (lowest/uncertain first), then deadline urgency
 * - Actions: Confirm, Edit & Confirm, Reject, Re-open
 * - Case number stamping (číslo jednací) onto Authorization
 * - Full audit trail with before/after state
 * 
 * Access: platform_admin/operator ONLY (Rule 17)
 * Bilingual: CS+EN UI strings under correspondence.review.* keys (Rule 8)
 * No emoji (Rule 19) - lucide-react icons only
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { 
  Mail, Search, Filter, Clock, CheckCircle, XCircle,
  AlertTriangle, RotateCcw, Eye, Edit, ChevronDown, ChevronRight,
  FileText, User, Plane, Shield, Calendar, History, Inbox,
  RefreshCw, ExternalLink, AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";
import {
  Alert, AlertDescription, AlertTitle,
} from "../../components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";

// Confidence badge color helper
const getConfidenceBadge = (confidence, t) => {
  if (confidence >= 0.8) {
    return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">{t("correspondence.review.highConfidence")}</Badge>;
  } else if (confidence >= 0.5) {
    return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">{t("correspondence.review.mediumConfidence")}</Badge>;
  } else {
    return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">{t("correspondence.review.lowConfidence")}</Badge>;
  }
};

// Format date helper
const formatDate = (dateStr, language) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Days until deadline badge
const DeadlineBadge = ({ daysUntil, t }) => {
  if (daysUntil === null || daysUntil === undefined) return null;
  
  if (daysUntil < 0) {
    return <Badge variant="destructive">{t("correspondence.review.overdue")}</Badge>;
  } else if (daysUntil === 0) {
    return <Badge variant="destructive">{t("correspondence.review.dueToday")}</Badge>;
  } else if (daysUntil <= 3) {
    return <Badge className="bg-orange-500">{daysUntil} {t("correspondence.review.daysLeft")}</Badge>;
  } else if (daysUntil <= 14) {
    return <Badge className="bg-yellow-500">{daysUntil} {t("correspondence.review.daysLeft")}</Badge>;
  }
  return <Badge variant="outline">{daysUntil} {t("correspondence.review.daysLeft")}</Badge>;
};

export default function CorrespondenceReviewQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { translate: t, language } = useLanguage();
  
  // Data state
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  
  // Modal states
  const [confirmDialog, setConfirmDialog] = useState({ open: false, item: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, item: null });
  const [reopenDialog, setReopenDialog] = useState({ open: false, item: null });
  const [auditDialog, setAuditDialog] = useState({ open: false, item: null, entries: [] });
  
  // Form states for confirm dialog
  const [confirmForm, setConfirmForm] = useState({
    authorization_id: "",
    mission_id: "",
    pilot_id: "",
    case_number: "",
    case_number_conflict_resolution: null,
    override_reference_number: "",
    override_deadline: "",
    override_priority: "",
    override_summary_cs: "",
    override_summary_en: "",
    review_notes: ""
  });
  
  // Reject form
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  
  // Reopen form
  const [reopenReason, setReopenReason] = useState("");
  
  // Case number conflict state
  const [caseNumberConflict, setCaseNumberConflict] = useState(null);
  
  // Action in progress
  const [actionLoading, setActionLoading] = useState(false);

  // Check access - platform_admin, operator, or organization_admin only
  const hasAccess = user && ["platform_admin", "operator", "organization_admin"].includes(user.role);
  
  // Fetch queue
  const fetchQueue = useCallback(async () => {
    if (!hasAccess) return;
    
    setLoading(true);
    try {
      const [queueRes, statsRes] = await Promise.all([
        api.get("/admin/correspondence-review/queue?limit=100"),
        api.get("/admin/correspondence-review/stats")
      ]);
      setQueue(queueRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch review queue:", error);
      if (error.response?.status === 403) {
        toast.error(language === "cs" 
          ? "Přístup odepřen. Vyžadována role platform_admin nebo operator."
          : "Access denied. Platform admin or operator role required."
        );
      } else {
        toast.error(language === "cs" ? "Chyba při načítání fronty" : "Failed to load queue");
      }
    } finally {
      setLoading(false);
    }
  }, [hasAccess, language]);
  
  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);
  
  // Open confirm dialog
  const openConfirmDialog = (item) => {
    setConfirmForm({
      authorization_id: item.proposed_authorization_id || "",
      mission_id: item.proposed_mission_id || "",
      pilot_id: item.proposed_pilot_id || "",
      case_number: item.extracted?.reference_number || "",
      case_number_conflict_resolution: null,
      override_reference_number: "",
      override_deadline: "",
      override_priority: "",
      override_summary_cs: "",
      override_summary_en: "",
      review_notes: ""
    });
    setCaseNumberConflict(null);
    setConfirmDialog({ open: true, item });
  };
  
  // Handle confirm action
  const handleConfirm = async () => {
    const item = confirmDialog.item;
    if (!item) return;
    
    setActionLoading(true);
    try {
      const payload = {
        authorization_id: confirmForm.authorization_id || null,
        mission_id: confirmForm.mission_id || null,
        pilot_id: confirmForm.pilot_id || null,
        case_number: confirmForm.case_number || null,
        review_notes: confirmForm.review_notes || null
      };
      
      // Add conflict resolution if present
      if (caseNumberConflict && confirmForm.case_number_conflict_resolution) {
        payload.case_number_conflict_resolution = confirmForm.case_number_conflict_resolution;
      }
      
      // Add overrides if set
      if (confirmForm.override_reference_number) {
        payload.override_reference_number = confirmForm.override_reference_number;
      }
      if (confirmForm.override_deadline) {
        payload.override_deadline = confirmForm.override_deadline;
      }
      if (confirmForm.override_priority) {
        payload.override_priority = confirmForm.override_priority;
      }
      if (confirmForm.override_summary_cs) {
        payload.override_summary_cs = confirmForm.override_summary_cs;
      }
      if (confirmForm.override_summary_en) {
        payload.override_summary_en = confirmForm.override_summary_en;
      }
      
      const response = await api.post(`/admin/correspondence-review/item/${item.id}/confirm`, payload);
      
      toast.success(language === "cs" 
        ? "Korespondence úspěšně potvrzena"
        : "Correspondence confirmed successfully"
      );
      
      // Show case number stamping result
      if (response.data.case_number_stamped) {
        toast.success(language === "cs"
          ? `Číslo jednací '${confirmForm.case_number}' zapsáno na autorizaci`
          : `Case number '${confirmForm.case_number}' stamped on authorization`
        );
      }
      
      setConfirmDialog({ open: false, item: null });
      fetchQueue();
      
    } catch (error) {
      console.error("Confirm failed:", error);
      
      // Handle case number conflict
      if (error.response?.status === 409 && error.response?.data?.error === "case_number_conflict") {
        setCaseNumberConflict({
          existing: error.response.data.existing_case_number,
          proposed: error.response.data.proposed_case_number
        });
        toast.error(language === "cs"
          ? `Konflikt čísla jednacího: autorizace již má '${error.response.data.existing_case_number}'`
          : `Case number conflict: authorization already has '${error.response.data.existing_case_number}'`
        );
      } else {
        const msg = error.response?.data?.message_cs || error.response?.data?.message_en || error.message;
        toast.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle reject action
  const handleReject = async () => {
    const item = rejectDialog.item;
    if (!item || !rejectReason || rejectReason.length < 5) {
      toast.error(language === "cs" ? "Vyplňte důvod zamítnutí (min. 5 znaků)" : "Enter rejection reason (min 5 chars)");
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post(`/admin/correspondence-review/item/${item.id}/reject`, {
        reason: rejectReason,
        review_notes: rejectNotes || null
      });
      
      toast.success(language === "cs" ? "Korespondence zamítnuta" : "Correspondence rejected");
      setRejectDialog({ open: false, item: null });
      setRejectReason("");
      setRejectNotes("");
      fetchQueue();
      
    } catch (error) {
      console.error("Reject failed:", error);
      const msg = error.response?.data?.message_cs || error.response?.data?.message_en || error.message;
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle reopen action
  const handleReopen = async () => {
    const item = reopenDialog.item;
    if (!item || !reopenReason || reopenReason.length < 5) {
      toast.error(language === "cs" ? "Vyplňte důvod znovuotevření (min. 5 znaků)" : "Enter reopen reason (min 5 chars)");
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post(`/admin/correspondence-review/item/${item.id}/reopen`, {
        reason: reopenReason
      });
      
      toast.success(language === "cs" ? "Korespondence znovu otevřena" : "Correspondence re-opened");
      setReopenDialog({ open: false, item: null });
      setReopenReason("");
      fetchQueue();
      
    } catch (error) {
      console.error("Reopen failed:", error);
      const msg = error.response?.data?.message_cs || error.response?.data?.message_en || error.message;
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Fetch audit trail
  const fetchAuditTrail = async (item) => {
    try {
      const response = await api.get(`/admin/correspondence-review/audit/${item.id}`);
      setAuditDialog({ open: true, item, entries: response.data });
    } catch (error) {
      console.error("Failed to fetch audit trail:", error);
      toast.error(language === "cs" ? "Chyba při načítání auditu" : "Failed to load audit trail");
    }
  };
  
  // Access denied view
  if (!hasAccess) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{language === "cs" ? "Přístup odepřen" : "Access Denied"}</AlertTitle>
          <AlertDescription>
            {language === "cs" 
              ? "Tato stránka je dostupná pouze pro platform_admin nebo operator."
              : "This page is only available for platform_admin or operator roles."
            }
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="correspondence-review-queue">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="review-queue-title">
            {t("correspondence.review.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("correspondence.review.subtitle")}
          </p>
        </div>
        <Button onClick={fetchQueue} variant="outline" size="sm" data-testid="refresh-queue-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("common.refresh") || "Refresh"}
        </Button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-pending">
            <CardHeader className="pb-2">
              <CardDescription>{t("correspondence.review.statsPending")}</CardDescription>
              <CardTitle className="text-3xl">{stats.pending}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-urgent">
            <CardHeader className="pb-2">
              <CardDescription>{t("correspondence.review.statsUrgent")}</CardDescription>
              <CardTitle className="text-3xl text-orange-600">{stats.urgent_with_deadline}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-low-confidence">
            <CardHeader className="pb-2">
              <CardDescription>{t("correspondence.review.statsLowConf")}</CardDescription>
              <CardTitle className="text-3xl text-red-600">{stats.low_confidence}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="stat-reviewed-today">
            <CardHeader className="pb-2">
              <CardDescription>{t("correspondence.review.statsReviewedToday")}</CardDescription>
              <CardTitle className="text-3xl text-green-600">{stats.reviewed_today}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}
      
      {/* HITL Warning Banner */}
      <Alert data-testid="hitl-warning">
        <Shield className="h-4 w-4" />
        <AlertTitle>{t("correspondence.review.hitlTitle")}</AlertTitle>
        <AlertDescription>
          {t("correspondence.review.hitlDescription")}
        </AlertDescription>
      </Alert>
      
      {/* Queue List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5" />
            {t("correspondence.review.queueTitle")}
            <Badge variant="secondary" className="ml-2">{queue.length}</Badge>
          </CardTitle>
          <CardDescription>
            {t("correspondence.review.queueDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : queue.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="empty-queue">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium">{t("correspondence.review.emptyQueue")}</p>
              <p>{t("correspondence.review.emptyQueueDesc")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {queue.map((item) => (
                <div 
                  key={item.id} 
                  className="border rounded-lg overflow-hidden"
                  data-testid={`queue-item-${item.id}`}
                >
                  {/* Item Header - Always visible */}
                  <div 
                    className="p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {expandedItem === item.id ? (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{item.subject}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {item.sender_name || item.sender_email} | {formatDate(item.received_at, language)}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            {getConfidenceBadge(item.link_confidence || 0, t)}
                            <DeadlineBadge daysUntil={item.days_until_deadline} t={t} />
                            <Badge variant="outline">
                              {language === "cs" ? item.source_name_cs : item.source_name_en}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Quick actions */}
                        <Button 
                          size="sm" 
                          variant="default"
                          onClick={(e) => { e.stopPropagation(); openConfirmDialog(item); }}
                          data-testid={`confirm-btn-${item.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t("correspondence.review.confirm")}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={(e) => { e.stopPropagation(); setRejectDialog({ open: true, item }); }}
                          data-testid={`reject-btn-${item.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          {t("correspondence.review.reject")}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {expandedItem === item.id && (
                    <div className="p-4 border-t">
                      <Tabs defaultValue="email" className="w-full">
                        <TabsList>
                          <TabsTrigger value="email">{t("correspondence.review.tabEmail")}</TabsTrigger>
                          <TabsTrigger value="extracted">{t("correspondence.review.tabExtracted")}</TabsTrigger>
                          <TabsTrigger value="links">{t("correspondence.review.tabLinks")}</TabsTrigger>
                          <TabsTrigger value="audit">{t("correspondence.review.tabAudit")}</TabsTrigger>
                        </TabsList>
                        
                        {/* Email Tab */}
                        <TabsContent value="email" className="mt-4">
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <Label className="text-muted-foreground">{t("correspondence.from")}</Label>
                                <p>{item.sender_name ? `${item.sender_name} <${item.sender_email}>` : item.sender_email}</p>
                              </div>
                              <div>
                                <Label className="text-muted-foreground">{t("correspondence.received")}</Label>
                                <p>{formatDate(item.received_at, language)}</p>
                              </div>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">{t("correspondence.body")}</Label>
                              <div className="mt-2 p-4 bg-muted/50 rounded-lg max-h-64 overflow-y-auto whitespace-pre-wrap text-sm font-mono">
                                {item.body_text || "(empty)"}
                              </div>
                            </div>
                          </div>
                        </TabsContent>
                        
                        {/* Extracted Data Tab */}
                        <TabsContent value="extracted" className="mt-4">
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-muted-foreground">{t("correspondence.referenceNumber")}</Label>
                                <p className="font-mono">{item.extracted?.reference_number || "-"}</p>
                              </div>
                              <div>
                                <Label className="text-muted-foreground">{t("correspondence.deadline")}</Label>
                                <p>{item.extracted?.deadline ? formatDate(item.extracted.deadline, language) : "-"}</p>
                              </div>
                              <div>
                                <Label className="text-muted-foreground">{t("correspondence.priority")}</Label>
                                <p>{item.extracted?.priority || "-"}</p>
                              </div>
                              <div>
                                <Label className="text-muted-foreground">{t("correspondence.actionRequired")}</Label>
                                <p>{item.extracted?.action_required || "-"}</p>
                              </div>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">{t("correspondence.summary")} (CS)</Label>
                              <p className="mt-1">{item.extracted?.summary_cs || "-"}</p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">{t("correspondence.summary")} (EN)</Label>
                              <p className="mt-1">{item.extracted?.summary_en || "-"}</p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Raw Extraction JSON</Label>
                              <pre className="mt-2 p-3 bg-muted/50 rounded text-xs overflow-x-auto">
                                {JSON.stringify(item.extracted?.raw_extraction || {}, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </TabsContent>
                        
                        {/* Links Tab */}
                        <TabsContent value="links" className="mt-4">
                          <div className="space-y-4">
                            {/* Proposed Authorization */}
                            <div className="p-3 border rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Shield className="h-4 w-4 text-blue-600" />
                                <Label>{t("correspondence.review.proposedAuth")}</Label>
                                <Badge variant="outline" className="text-xs">{t("correspondence.proposed")}</Badge>
                              </div>
                              {item.proposed_authorization ? (
                                <div className="pl-6 text-sm">
                                  <p className="font-medium">{item.proposed_authorization.reference_number}</p>
                                  <p className="text-muted-foreground">{item.proposed_authorization.title}</p>
                                  {item.proposed_authorization.case_number && (
                                    <p className="text-muted-foreground">
                                      {t("correspondence.caseNumber")}: {item.proposed_authorization.case_number}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <p className="pl-6 text-sm text-muted-foreground">-</p>
                              )}
                            </div>
                            
                            {/* Proposed Mission */}
                            <div className="p-3 border rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Plane className="h-4 w-4 text-green-600" />
                                <Label>{t("correspondence.review.proposedMission")}</Label>
                                <Badge variant="outline" className="text-xs">{t("correspondence.proposed")}</Badge>
                              </div>
                              {item.proposed_mission ? (
                                <div className="pl-6 text-sm">
                                  <p className="font-medium">{item.proposed_mission.name}</p>
                                  <p className="text-muted-foreground">{item.proposed_mission.status}</p>
                                </div>
                              ) : (
                                <p className="pl-6 text-sm text-muted-foreground">-</p>
                              )}
                            </div>
                            
                            {/* Proposed Pilot */}
                            <div className="p-3 border rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <User className="h-4 w-4 text-purple-600" />
                                <Label>{t("correspondence.review.proposedPilot")}</Label>
                                <Badge variant="outline" className="text-xs">{t("correspondence.proposed")}</Badge>
                              </div>
                              {item.proposed_pilot ? (
                                <div className="pl-6 text-sm">
                                  <p className="font-medium">{item.proposed_pilot.first_name} {item.proposed_pilot.last_name}</p>
                                  <p className="text-muted-foreground">{item.proposed_pilot.email}</p>
                                </div>
                              ) : (
                                <p className="pl-6 text-sm text-muted-foreground">-</p>
                              )}
                            </div>
                          </div>
                        </TabsContent>
                        
                        {/* Audit Tab */}
                        <TabsContent value="audit" className="mt-4">
                          <Button 
                            variant="outline" 
                            onClick={() => fetchAuditTrail(item)}
                            data-testid={`view-audit-btn-${item.id}`}
                          >
                            <History className="h-4 w-4 mr-2" />
                            {t("correspondence.review.loadAudit")}
                          </Button>
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* ===================== CONFIRM DIALOG ===================== */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, item: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t("correspondence.review.confirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("correspondence.review.confirmDescription")}
            </DialogDescription>
          </DialogHeader>
          
          {confirmDialog.item && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{confirmDialog.item.subject}</p>
                <p className="text-sm text-muted-foreground">{confirmDialog.item.sender_email}</p>
              </div>
              
              {/* Case Number Conflict Alert */}
              {caseNumberConflict && (
                <Alert variant="destructive" data-testid="case-number-conflict-alert">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>{t("correspondence.review.caseNumberConflict")}</AlertTitle>
                  <AlertDescription>
                    {language === "cs"
                      ? `Autorizace již má číslo jednací '${caseNumberConflict.existing}'. Zvolte akci.`
                      : `Authorization already has case number '${caseNumberConflict.existing}'. Choose action.`
                    }
                  </AlertDescription>
                  <div className="mt-4 flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setConfirmForm({...confirmForm, case_number_conflict_resolution: "keep_existing"})}
                      className={confirmForm.case_number_conflict_resolution === "keep_existing" ? "ring-2 ring-primary" : ""}
                    >
                      {t("correspondence.review.keepExisting")}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => setConfirmForm({...confirmForm, case_number_conflict_resolution: "overwrite"})}
                      className={confirmForm.case_number_conflict_resolution === "overwrite" ? "ring-2 ring-white" : ""}
                    >
                      {t("correspondence.review.overwrite")}
                    </Button>
                  </div>
                </Alert>
              )}
              
              {/* Links Section */}
              <div className="space-y-4">
                <h4 className="font-medium">{t("correspondence.review.linksSection")}</h4>
                
                <div className="grid gap-4">
                  <div>
                    <Label>{t("correspondence.review.authorizationId")}</Label>
                    <Input 
                      value={confirmForm.authorization_id}
                      onChange={(e) => setConfirmForm({...confirmForm, authorization_id: e.target.value})}
                      placeholder={confirmDialog.item.proposed_authorization_id || "auth_..."}
                      data-testid="confirm-auth-id-input"
                    />
                    {confirmDialog.item.proposed_authorization && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("correspondence.review.proposed")}: {confirmDialog.item.proposed_authorization.reference_number}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label>{t("correspondence.review.missionId")}</Label>
                    <Input 
                      value={confirmForm.mission_id}
                      onChange={(e) => setConfirmForm({...confirmForm, mission_id: e.target.value})}
                      placeholder={confirmDialog.item.proposed_mission_id || "mission_..."}
                      data-testid="confirm-mission-id-input"
                    />
                  </div>
                  
                  <div>
                    <Label>{t("correspondence.review.pilotId")}</Label>
                    <Input 
                      value={confirmForm.pilot_id}
                      onChange={(e) => setConfirmForm({...confirmForm, pilot_id: e.target.value})}
                      placeholder={confirmDialog.item.proposed_pilot_id || "pilot_..."}
                      data-testid="confirm-pilot-id-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Case Number Stamping */}
              <div className="space-y-2">
                <Label>{t("correspondence.review.caseNumberLabel")}</Label>
                <Input 
                  value={confirmForm.case_number}
                  onChange={(e) => setConfirmForm({...confirmForm, case_number: e.target.value})}
                  placeholder="UCL/12345/2024"
                  data-testid="confirm-case-number-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t("correspondence.review.caseNumberHint")}
                </p>
              </div>
              
              {/* Override Section (Accordion) */}
              <Accordion type="single" collapsible>
                <AccordionItem value="overrides">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <Edit className="h-4 w-4" />
                      {t("correspondence.review.editOverrides")}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>{t("correspondence.review.overrideRefNumber")}</Label>
                        <Input 
                          value={confirmForm.override_reference_number}
                          onChange={(e) => setConfirmForm({...confirmForm, override_reference_number: e.target.value})}
                          placeholder={confirmDialog.item.extracted?.reference_number || ""}
                        />
                      </div>
                      <div>
                        <Label>{t("correspondence.review.overrideDeadline")}</Label>
                        <Input 
                          type="datetime-local"
                          value={confirmForm.override_deadline}
                          onChange={(e) => setConfirmForm({...confirmForm, override_deadline: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>{t("correspondence.review.overridePriority")}</Label>
                        <Select 
                          value={confirmForm.override_priority} 
                          onValueChange={(v) => setConfirmForm({...confirmForm, override_priority: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t("correspondence.review.selectPriority")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t("correspondence.review.overrideSummaryCs")}</Label>
                        <Textarea 
                          value={confirmForm.override_summary_cs}
                          onChange={(e) => setConfirmForm({...confirmForm, override_summary_cs: e.target.value})}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>{t("correspondence.review.overrideSummaryEn")}</Label>
                        <Textarea 
                          value={confirmForm.override_summary_en}
                          onChange={(e) => setConfirmForm({...confirmForm, override_summary_en: e.target.value})}
                          rows={2}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              
              {/* Review Notes */}
              <div>
                <Label>{t("correspondence.review.notes")}</Label>
                <Textarea 
                  value={confirmForm.review_notes}
                  onChange={(e) => setConfirmForm({...confirmForm, review_notes: e.target.value})}
                  placeholder={t("correspondence.review.notesPlaceholder")}
                  rows={2}
                  data-testid="confirm-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, item: null })} disabled={actionLoading}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleConfirm} disabled={actionLoading} data-testid="confirm-submit-btn">
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              {t("correspondence.review.confirmAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* ===================== REJECT DIALOG ===================== */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, item: null })}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>{t("correspondence.review.rejectTitle")}</DialogTitle>
            <DialogDescription>
              {t("correspondence.review.rejectDescription")}
            </DialogDescription>
          </DialogHeader>
          
          {rejectDialog.item && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{rejectDialog.item.subject}</p>
                <p className="text-sm text-muted-foreground">{rejectDialog.item.sender_email}</p>
              </div>
              
              <div>
                <Label>{t("correspondence.review.rejectReason")} *</Label>
                <Textarea 
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t("correspondence.review.rejectReasonPlaceholder")}
                  rows={3}
                  data-testid="reject-reason-input"
                />
                <p className="text-xs text-muted-foreground mt-1">{t("correspondence.review.minChars")}</p>
              </div>
              
              <div>
                <Label>{t("correspondence.review.notes")}</Label>
                <Textarea 
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder={t("correspondence.review.notesPlaceholder")}
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, item: null })} disabled={actionLoading}>
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading || rejectReason.length < 5} data-testid="reject-submit-btn">
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              {t("correspondence.review.rejectAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* ===================== REOPEN DIALOG ===================== */}
      <Dialog open={reopenDialog.open} onOpenChange={(open) => !open && setReopenDialog({ open: false, item: null })}>
        <DialogContent data-testid="reopen-dialog">
          <DialogHeader>
            <DialogTitle>{t("correspondence.review.reopenTitle")}</DialogTitle>
            <DialogDescription>
              {t("correspondence.review.reopenDescription")}
            </DialogDescription>
          </DialogHeader>
          
          {reopenDialog.item && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{reopenDialog.item.subject}</p>
              </div>
              
              <div>
                <Label>{t("correspondence.review.reopenReason")} *</Label>
                <Textarea 
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder={t("correspondence.review.reopenReasonPlaceholder")}
                  rows={3}
                  data-testid="reopen-reason-input"
                />
                <p className="text-xs text-muted-foreground mt-1">{t("correspondence.review.minChars")}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenDialog({ open: false, item: null })} disabled={actionLoading}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleReopen} disabled={actionLoading || reopenReason.length < 5} data-testid="reopen-submit-btn">
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              {t("correspondence.review.reopenAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* ===================== AUDIT DIALOG ===================== */}
      <Dialog open={auditDialog.open} onOpenChange={(open) => !open && setAuditDialog({ open: false, item: null, entries: [] })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle>{t("correspondence.review.auditTitle")}</DialogTitle>
            <DialogDescription>
              {auditDialog.item?.subject}
            </DialogDescription>
          </DialogHeader>
          
          {auditDialog.entries.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">{t("correspondence.review.noAuditEntries")}</p>
          ) : (
            <div className="space-y-4">
              {auditDialog.entries.map((entry) => (
                <div key={entry.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={
                      entry.action === "confirm" ? "default" :
                      entry.action === "edit_confirm" ? "secondary" :
                      entry.action === "reject" ? "destructive" :
                      "outline"
                    }>
                      {entry.action}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(entry.timestamp, language)}
                    </span>
                  </div>
                  <p className="text-sm">
                    <span className="text-muted-foreground">{t("correspondence.review.auditOperator")}:</span> {entry.operator_email}
                  </p>
                  {entry.reason && (
                    <p className="text-sm mt-1">
                      <span className="text-muted-foreground">{t("correspondence.review.auditReason")}:</span> {entry.reason}
                    </p>
                  )}
                  {entry.changes_summary && entry.changes_summary.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground mb-1">{t("correspondence.review.auditChanges")}:</p>
                      <ul className="text-xs list-disc list-inside">
                        {entry.changes_summary.map((change, idx) => (
                          <li key={idx}>{change}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditDialog({ open: false, item: null, entries: [] })}>
              {t("common.close") || "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
